/**
 * The renter-facing support widget. The thread and composer inside it are
 * SupportChatThread, the shared @gearghost/messaging-ui components, loaded
 * the first time the chat opens: the widget is on every page and the chat
 * is opened on few of them. The panel's frame draws at once, with the
 * thread's loading line until the thread arrives.
 *
 * Only the panel chrome and the open wiring are local; that shell is what
 * makes it a widget rather than a page. Open is store state
 * (redux/slices/supportChat): the header's support icon (inc/headerMenu)
 * toggles it and carries the unread count, and the order page's Request
 * a coverage increase action opens the chat after its message lands.
 * The widget's own launcher, a bubble floating at the bottom right, moved
 * into the header (Tyler, 2026-10-09: nothing floats over a page's bottom),
 * and came back for the pages that have no header (the sign-up steps,
 * check-email, the 404), where it is the only way in. The widget draws it
 * only while no header icon is on the page (supportChat headerLaunchers).
 */
import { lazy, Suspense } from 'react';
import { MessageCircleQuestion, X } from 'lucide-react';
import useIsAuthenticated from '@/hooks/useAuth';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { closeSupportChat, toggleSupportChat } from '@/redux/slices/supportChat';
import { useGetSupportUnreadQuery } from '@/redux/api/conversationApi';
import { badgeCount } from '@/utils/badgeCount';
import { importWithRetry } from '@/navigation/utils/lazyRoute';
import styles from './supportChat.module.scss';

// Retried, and the page reloaded once when a deploy has replaced the
// chunk, the same way the routes load their pages.
const SupportChatThread = lazy(() => importWithRetry(() => import('./SupportChatThread')));

export default function SupportChatWidget() {
  const isAuthenticated = useIsAuthenticated();
  const open = useAppSelector((state) => state.supportChat.open);
  // No header on the page, so no header icon: the bubble is the way in.
  const showBubble = useAppSelector((state) => state.supportChat.headerLaunchers === 0);
  const dispatch = useAppDispatch();

  // The bubble's count (the header icon reads the same query when it is
  // the launcher). Read-only on the backend; useUnreadCountSocketSync
  // refreshes it live, and reading the thread clears it.
  const { data: supportUnread } = useGetSupportUnreadQuery(undefined, {
    skip: !isAuthenticated || !showBubble,
  });

  if (!isAuthenticated || (!open && !showBubble)) return null;

  return (
    <>
      {open && (
        // ggmWeb supplies the --ggm-* token mapping the shared messaging
        // components style against (same class the Messages page uses).
        // Over the bubble it opens above it, as it always did.
        <div
          className={`${styles.panel} ${showBubble ? styles.panelOverBubble : ''} ggmWeb`}
          data-testid="support-chat-panel"
        >
          <div className={styles.header}>
            <h3 className={styles.headerTitle}>Support</h3>
            <button
              className={styles.closeBtn}
              onClick={() => dispatch(closeSupportChat())}
              aria-label="Close support chat"
              data-testid="support-chat-close"
            >
              <X size={18} />
            </button>
          </div>

          <Suspense fallback={<div className={styles.loading}>Loading...</div>}>
            <SupportChatThread />
          </Suspense>
        </div>
      )}

      {showBubble && (
        <button
          className={styles.bubble}
          onClick={() => dispatch(toggleSupportChat())}
          aria-label="Open support chat"
          aria-expanded={open}
          data-testid="support-chat-bubble"
        >
          <MessageCircleQuestion size={26} />
          {!open && supportUnread && supportUnread.unreadCount > 0 && (
            <span className={styles.badgeCount} data-testid="support-chat-bubble-count">
              {badgeCount(supportUnread.unreadCount)}
            </span>
          )}
        </button>
      )}
    </>
  );
}
